import React, { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import {
  AccountBalanceWalletOutlined,
  ErrorOutline,
  Refresh,
  SchoolOutlined,
} from "@material-ui/icons";
import { useParent } from "../../../../context/ParentContext";
import { baseUrl } from "../../../../services/config";
import "../MonthlyStudentAttendance/parent-progress.css";
import "./fee-status.css";

const getStudentId = (child) =>
  child?.student?.id || child?.student_id || child?.id || null;
const getClassId = (child) =>
  child?.student_class_id ||
  child?.student?.student_class_id ||
  child?.student?.student_class?.id ||
  null;

function getChallans(result) {
  const payload = result?.payload || result || {};
  return Array.isArray(payload.fee_challans) ? payload.fee_challans : [];
}

function formatMoney(value) {
  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-PK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function getFeeMonth(challan) {
  const value = challan?.fee_challan_details?.find(
    (detail) => detail.fee_month,
  )?.fee_month;
  if (!value) return "Fee challan";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? "Fee challan"
    : new Intl.DateTimeFormat("en-PK", {
        month: "long",
        year: "numeric",
      }).format(date);
}

function getReadStatus(challan) {
  return (
    challan?.is_read ??
    challan?.notification?.is_read ??
    challan?.fee_challan_notification?.is_read
  );
}

export default function FeeStatus() {
  const alert = useAlert();
  const { selectedChild, setUnreadFeeCount } = useParent();
  const [sessions, setSessions] = useState([]);
  const [yearId, setYearId] = useState("");
  const [unpaid, setUnpaid] = useState([]);
  const [paid, setPaid] = useState([]);
  const [activeTab, setActiveTab] = useState("unpaid");
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [paidLoading, setPaidLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [unpaidError, setUnpaidError] = useState("");
  const [paidError, setPaidError] = useState("");
  const [sessionError, setSessionError] = useState("");
  const studentId = getStudentId(selectedChild);
  const classId = getClassId(selectedChild);
  const studentName = selectedChild?.student?.name || "Student";

  const authHeaders = (json = false) => {
    const token = localStorage.getItem("parentToken");
    return {
      Accept: "application/json",
      ...(json ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const markFeeNotificationsRead = async () => {
    try {
      const response = await fetch(
        `${baseUrl}/set_fee_challan_notifications?student_id=${encodeURIComponent(
          studentId,
        )}`,
        { headers: authHeaders() },
      );
      if (response.ok) setUnreadFeeCount(0);
    } catch (markReadError) {
      // Keep the successfully loaded fee records visible if marking read fails.
    }
  };

  const loadSessions = async () => {
    setSessionLoading(true);
    setSessionError("");
    try {
      const response = await fetch(`${baseUrl}/session`, {
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load sessions.");
      const availableSessions = Array.isArray(result?.payload)
        ? result.payload
        : [];
      setSessions(availableSessions);
      const activeSession = availableSessions.find(
        (session) => String(session.active_academic_year) === "1",
      );
      setYearId((current) =>
        current || String(activeSession?.id || availableSessions[0]?.id || ""),
      );
    } catch (requestError) {
      setSessions([]);
      const message = requestError.message || "Unable to load sessions.";
      setSessionError(message);
      alert.error(message);
    } finally {
      setSessionLoading(false);
    }
  };

  const loadUnpaidFees = async () => {
    if (!studentId) return;
    setUnpaidLoading(true);
    setUnpaidError("");
    try {
      const response = await fetch(
        `${baseUrl}/parent-student-unpaid-fee-history/${studentId}`,
        { headers: authHeaders() },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load unpaid fees.");
      await markFeeNotificationsRead();
      const unpaidChallans = getChallans(result).filter(
        (challan) => Number(challan.status) === 0,
      );
      if (!unpaidChallans.length) {
        const message = "No unpaid fee found for this student.";
        setUnpaid([]);
        setUnpaidError(message);
        alert.show(message);
        return;
      }
      setUnpaid(unpaidChallans);
    } catch (requestError) {
      setUnpaid([]);
      const message = requestError.message || "Unable to load unpaid fees.";
      setUnpaidError(message);
      alert.error(message);
    } finally {
      setUnpaidLoading(false);
    }
  };

  const loadPaidFees = async () => {
    if (!studentId || !yearId || !classId) return;
    setPaidLoading(true);
    setPaidError("");
    try {
      const response = await fetch(
        `${baseUrl}/parent-student-paid-fee-history`,
        {
          method: "POST",
          headers: authHeaders(true),
          body: JSON.stringify({
            year_id: String(yearId),
            student_id: String(studentId),
            student_class_id: String(classId),
          }),
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load paid fees.");
      const paidChallans = getChallans(result).filter(
        (challan) => Number(challan.status) === 2,
      );
      if (!paidChallans.length) {
        const message = "No paid fee found for the selected session.";
        setPaid([]);
        setPaidError(message);
        alert.show(message);
        return;
      }
      setPaid(paidChallans);
    } catch (requestError) {
      setPaid([]);
      const message = requestError.message || "Unable to load paid fees.";
      setPaidError(message);
      alert.error(message);
    } finally {
      setPaidLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUnpaid([]);
    setPaid([]);
    loadUnpaidFees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => {
    setPaid([]);
    loadPaidFees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, yearId, classId]);

  const activeRecords = activeTab === "unpaid" ? unpaid : paid;
  const activeLoading = activeTab === "unpaid" ? unpaidLoading : paidLoading;
  const activeError = activeTab === "unpaid" ? unpaidError : paidError;
  const activeTotal = useMemo(
    () =>
      activeRecords.reduce(
        (sum, challan) =>
          sum +
          Number(
            activeTab === "paid"
              ? challan.paid || challan.payable
              : challan.payable,
          ),
        0,
      ),
    [activeRecords, activeTab],
  );

  const retryActiveTab =
    activeTab === "unpaid" ? loadUnpaidFees : loadPaidFees;

  return (
    <section className="parent-progress-page parent-fee-page">
      <div className="parent-progress-hero">
        <div>
          <span className="parent-progress-eyebrow">Student finance</span>
          <h1>Fee Status</h1>
          <p>
            {selectedChild
              ? `Fee challans and payment history for ${studentName}.`
              : "Select a child to view fee details."}
          </p>
        </div>
        <label className="parent-fee-session">
          <span>Academic session</span>
          <select
            value={yearId}
            onChange={(event) => setYearId(event.target.value)}
            disabled={sessionLoading || !sessions.length}
          >
            <option value="">
              {sessionLoading ? "Loading sessions..." : "Select session"}
            </option>
            {sessions.map((session) => (
              <option key={session.id} value={session.id}>
                {session.name || session.session_name || session.year || `Session ${session.id}`}
              </option>
            ))}
          </select>
          {sessionError && <small>{sessionError}</small>}
        </label>
      </div>

      {!studentId ? (
        <State
          icon={SchoolOutlined}
          title="No student selected"
          text="Please select a child from the top menu to view fee status."
        />
      ) : (
        <div className="parent-fee-panel">
          <div className="parent-fee-panel-head">
            <div className="parent-fee-tabs" role="tablist">
              <button
                className={activeTab === "unpaid" ? "active" : ""}
                onClick={() => setActiveTab("unpaid")}
                type="button"
              >
                Unpaid Fee <span>{unpaid.length}</span>
              </button>
              <button
                className={activeTab === "paid" ? "active" : ""}
                onClick={() => setActiveTab("paid")}
                type="button"
              >
                Paid Fee <span>{paid.length}</span>
              </button>
            </div>
            {!activeLoading && (
              <div className={`parent-fee-tab-total ${activeTab}`}>
                <span>{activeTab === "unpaid" ? "Outstanding" : "Paid total"}</span>
                <strong>{formatMoney(activeTotal)}</strong>
              </div>
            )}
          </div>

          {activeTab === "paid" && !yearId ? (
            <State
              icon={AccountBalanceWalletOutlined}
              title="Select an academic session"
              text="Choose a session above to load paid fee challans."
            />
          ) : activeLoading ? (
            <State
              loading
              title={`Loading ${activeTab} fees`}
              text={`Getting ${activeTab} challans for this student.`}
            />
          ) : activeError ? (
            <State
              icon={ErrorOutline}
              error
              title={`${activeTab === "unpaid" ? "Unpaid" : "Paid"} fees could not be loaded`}
              text={activeError}
              onRetry={retryActiveTab}
            />
          ) : activeRecords.length ? (
            <FeeTable records={activeRecords} paid={activeTab === "paid"} />
          ) : (
            <div className="parent-fee-empty">
              <AccountBalanceWalletOutlined />
              <h2>No {activeTab} challans</h2>
              <p>
                {activeTab === "unpaid"
                  ? "There are no outstanding fee challans."
                  : "No paid fee history is available for the selected session."}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function FeeTable({ records, paid }) {
  return (
    <div className="parent-fee-table-wrap">
      <table className="parent-fee-table">
        <thead>
          <tr>
            <th>Fee period</th><th>Challan</th><th>Fee details</th>
            <th>{paid ? "Received" : "Due date"}</th>
            <th className="amount">Amount</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {records.map((challan) => {
            const readStatus = getReadStatus(challan);
            return (
              <tr key={challan.id}>
                <td data-label="Fee period">
                  <strong>{getFeeMonth(challan)}</strong>
                  <small>Issued {formatDate(challan.issue_date)}</small>
                </td>
                <td data-label="Challan">
                  #{challan.challan_no || challan.id}
                </td>
                <td data-label="Fee details">
                  <div className="parent-fee-details">
                    {(challan.fee_challan_details || []).map((detail) => (
                      <span key={detail.id}>
                        {detail.fee_name} <b>{formatMoney(detail.amount)}</b>
                      </span>
                    ))}
                  </div>
                </td>
                <td data-label={paid ? "Received" : "Due date"}>
                  {formatDate(
                    paid ? challan.received_date : challan.due_date,
                  )}
                </td>
                <td data-label="Amount" className="amount">
                  <strong>
                    {formatMoney(paid ? challan.paid : challan.payable)}
                  </strong>
                </td>
                <td data-label="Status">
                  <div className="parent-fee-statuses">
                    <span
                      className={`parent-fee-badge ${
                        paid ? "paid" : "unpaid"
                      }`}
                    >
                      {paid ? "Paid" : "Unpaid"}
                    </span>
                    {readStatus !== undefined && readStatus !== null &&
                      (Number(readStatus) === 0 ? (
                        <span className="parent-progress-unread">New</span>
                      ) : (
                        <span className="parent-progress-read">Viewed</span>
                      ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function State({ icon: Icon, title, text, loading, error, onRetry }) {
  return (
    <div className={`parent-fee-state${error ? " error" : ""}`} role={error ? "alert" : "status"}>
      {loading ? <span className="parent-fee-spinner" /> : Icon && <Icon />}
      <h2>{title}</h2><p>{text}</p>
      {onRetry && <button type="button" onClick={onRetry}><Refresh /> Try again</button>}
    </div>
  );
}
